import { Box, Typography, Link, Grid, Divider, Button } from "@mui/material";
import React, { useEffect } from "react";
import BlackDot from "../components/BlackDot";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';

const ReleaseNotesPage: React.FC = React.memo(() => {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // Scroll to top smoothly when the page loads
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });

        // Handle hash navigation
        if (location.hash) {
            const id = location.hash.substring(1);
            const element = document.getElementById(id);
            if (element) {
                const headerOffset = 80;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth"
                });
            }
        }
    }, [location.pathname, location.hash]);

    return (
        <Box sx={{ paddingLeft: 4 }}>
            <Typography 
                sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: "1.5rem", sm: "1.8rem" },
                    color: "#0b3452",
                    display: 'inline-block',
                    marginBottom: 1.5,
                    lineHeight: 1.5,
                }}
            >
                Release Notes
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1.1rem" }}>
                This page contains information about the latest updates, new features, and improvements 
                made to Finvu's Account Aggregator ecosystem.
            </Typography>

            {/* March 2025 Section */}
            <Box id="march-2025">
                <Typography variant="h5" sx={{ mt: 5, color: "#0b3452", fontWeight: "bold" }}>
                    March 2025
                </Typography>
                
                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                    Enhanced User Experience through Real-Time Event Capture
                </Typography>
                
                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1rem" }}>
                    Get visibility of the user actions on Finvu AA by capturing real-time events. This means you can:
                </Typography>

                <Box component="ul" sx={{ mt: 1, color: "#555", pl: 3 }}>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            ✅ <Box component="span" sx={{ fontWeight: 'bold' }}>Proactively support your customers</Box> : Incase they drop-off midway.
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            ✅ <Box component="span" sx={{ fontWeight: 'bold' }}>Build guided user journeys</Box>: With personalised prompts for next actions.
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            ✅ <Box component="span" sx={{ fontWeight: 'bold' }}>Boost success rates</Box> : By Analysing friction points and making rapid improvements to enhance overall conversion.
                        </Typography>
                    </li>
                </Box>

                {/* AA Redirection Image */}
                <Box sx={{ mt: 4, mb: 3 }}>
                    <Box 
                        component="img"
                        src="/images/aa_new_redirection.png"
                        alt="AA Redirection Events"
                        sx={{
                            width: 'auto',
                            maxWidth: { xs: '85%', sm: '70%', md: '60%' },
                            height: 'auto',
                            borderRadius: '4px'
                        }}
                    />
                </Box>

                <Typography sx={{ mt: 2, mb: 2, fontStyle: 'bold', color: "#555" }}>
                    <Link href="https://docs.finvu.in/analytics" color="#1976d2" target="_blank" rel="noopener noreferrer"><strong>Integrate Now!</strong></Link>   
                </Typography>

                <Divider sx={{ mt: 5, mb: 3 }} />

                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                    FasTrack Support Experience APIs
                </Typography>
                
                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1rem" }}>
                    Skip the back-and-forth of sharing consentHandleIds with us and waiting for our analysis. Empower your support teams with direct access to your customers' consent and data journey lifecycle events, their statuses and error messages.
                </Typography>

                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontWeight: "medium" }}>
                    Key APIs & Events Accessible:
                </Typography>

                <Box component="ul" sx={{ mt: 1, color: "#555", pl: 3 }}>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Consent Flow APIs – FIU, AA, and FIP consent journey API interactions
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            User Flow APIs – User interactions on the AA Client
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Data Flow APIs – FIU, AA, and FIP data journey API interactions
                        </Typography>
                    </li>
                </Box>

                {/* FasTrack Image */}
                <Box sx={{ mt: 4, mb: 3 }}>
                    <Box 
                        component="img"
                        src="/images/fastrack.png"
                        alt="FasTrack Support Experience APIs"
                        sx={{
                            width: 'auto',
                            maxWidth: { xs: '85%', sm: '70%', md: '60%' },
                            height: 'auto',
                            borderRadius: '4px'
                        }}
                    />
                </Box>

                <Typography sx={{ lineHeight: 1.7, mt: 2 }}>
                    <Link href="https://docs.finvu.in/fastrack" color="#1976d2" target="_blank" rel="noopener noreferrer"><strong>Try Now!</strong></Link>
                </Typography>

                <Typography sx={{ mt: 2, mb: 2, fontStyle: 'italic', color: "#555" }}>
                    <strong>Note:</strong> To test these APIs you will need your Sahamati token (Client API Token) for authorization purposes. Refer <Link href="https://docs.finvu.in/api-integration" color="#1976d2" target="_blank" rel="noopener noreferrer">here</Link> to get access to your token. 
                </Typography>

                <Divider sx={{ mt: 5, mb: 3 }} />

                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                    Finvu Buddy - Automating daily FIP Health Updates
                </Typography>
                
                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1rem" }}>
                    Finvu Buddy, our bot, sends daily FIP performance reports straight to your WhatsApp—helping you resolve issues faster and optimize success rates by staying abreast of FIP downtimes and failures.
                </Typography>

                {/* Finvu Buddy Image */}
                <Box sx={{ mt: 4, mb: 3 }}>
                    <Box 
                        component="img"
                        src="/images/finvu_buddy_healthbot.png"
                        alt="Finvu Buddy Health Bot"
                        sx={{
                            width: 'auto',
                            maxWidth: { xs: '85%', sm: '70%', md: '50%' },
                            height: 'auto',
                            borderRadius: '4px'
                        }}
                    />
                </Box>
                
                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                    Email your WhatsApp number at <Link href="mailto:support@cookiejar.co.in" color="#1976d2" target="_blank" rel="noopener noreferrer">support@cookiejar.co.in</Link> to get Finvu Buddy set up for you!
                </Typography>

                <Divider sx={{ mt: 5, mb: 3 }} />

                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                    API-Driven Consent Revocation
                </Typography>
                
                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1rem" }}>
                    Revoking consent should be as seamless as granting it. Leverage our /Consent/Notification endpoint (<Link href="https://api.rebit.org.in/viewSpec/" color="#1976d2" target="_blank" rel="noopener noreferrer">ReBIT-specified</Link>) to effortlessly revoke customer consents.
                </Typography>

                <Box component="ul" sx={{ mt: 1, color: "#555", pl: 3 }}>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Resolve customer grievances faster
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Revoke and archive stale or unnecessary consents
                        </Typography>
                    </li>
                </Box>

                <Divider sx={{ mt: 5, mb: 3 }} />

                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                    Redirection AA Client (Sweecar) Enhancements
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}>
                    Guiding Users When Accounts Aren't Found
                </Typography>
                
                <Typography sx={{ mt: 1, color: "#555", lineHeight: 1.7 }}>
                    When an FIP returns a "RECORD-NOT-FOUND" status during discovery, we now display <Box component="span" sx={{ fontWeight: 'bold' }}>previously linked accounts</Box> instead of just an error message. This ensures users clearly see their next steps, reducing confusion and improving their experience.
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}>
                    More UI Customization for a Seamless Brand Experience
                </Typography>
                
                <Typography sx={{ mt: 1, color: "#555", lineHeight: 1.7 }}>
                    Finvu's redirection UI client now supports a wider range of fonts and makes almost all text on the AA client configurable.
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}>
                    No More Dead Ends: Customer's can discover Only Finvu-Live FIPs
                </Typography>
                <Typography sx={{ mt: 1, color: "#555", lineHeight: 1.7 }}>
                    Previously, the AA Client displayed all FIPs live in the AA ecosystem. Now, only FIPs integrated with Finvu are discoverable and linkable, ensuring a better user experience with fewer dead ends.
                </Typography>
                <Typography sx={{ mt: 1, color: "#555", lineHeight: 1.7, fontStyle: 'italic' }}>
                    <strong>Note:</strong> The enhancements are available to all the FIUs who are currently on v2 (and onwards) of our redirection AA Client. Reach out to us here <Link href="mailto:support@cookiejar.co.in" color="#1976d2" target="_blank" rel="noopener noreferrer">support@cookiejar.co.in</Link> if you are still not on v2. 
                </Typography>

                <Divider sx={{ mt: 5, mb: 3 }} />

                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                    Bug Fixes
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                    Missing Failure Notifications for FI Fetch
                </Typography>
                <Typography sx={{ mt: 1, color: "#555", lineHeight: 1.7 }}>
                    Resolved issues pertaining to the intermittent failure in the delivery of FI failure notifications to FIUs, ensuring consistent and reliable communication.
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                    FI Fetch Issue - "Data Not Found" Error for Some Accounts
                </Typography>
                <Typography sx={{ mt: 1, color: "#555", lineHeight: 1.7 }}>
                    We've resolved an issue where some accounts returned a "Data Not Found" error, even when data was successfully fetched. FIUs can now expect more consistent and reliable data retrieval.
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                    New Users Unable to Auto-Discover Accounts
                </Typography>
                <Typography sx={{ mt: 1, color: "#555", lineHeight: 1.7 }}>
                    Resolved an issue preventing new users from automatically discovering their accounts.
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                    Inconsistent Session Management Across WebSocket Pods
                </Typography>
                <Typography sx={{ mt: 1, color: "#555", lineHeight: 1.7 }}>
                    We've fixed an issue where OTP verification could fail if a client reconnected to a different pod after initiating login. Now, with distributed caching for pre-login sessions, users can seamlessly continue their authentication process without disruptions.
                </Typography>

                <Box sx={{ mt: 5, mb: 3, py: 2, px: 3, backgroundColor: "#f8f9fa", borderRadius: 2, border: "1px solid #e0e0e0" }}>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, textAlign: "center" }}>
                        For any questions or support, reach out to our team at <Link href="mailto:support@finvu.in" color="#1976d2">support@finvu.in</Link>.
                    </Typography>
                </Box>
            </Box>

            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                mt: 6,
                mb: 4,
                borderTop: '1px solid #e0e0e0',
                pt: 4
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                        navigate('/');
                        setTimeout(() => {
                            window.scrollTo({
                                top: 0,
                                behavior: 'smooth'
                            });
                        }, 100);
                    }}
                    sx={{ color: '#0b3452' }}
                >
                    Back to Home
                </Button>
            </Box>
        </Box>
    );
});

export default ReleaseNotesPage; 